/**
 * Утилиты каталога для страницы вендора (/solutions/:vendorSlug).
 */

import { ensureArray } from './utils';

export function slugifyVendorName(name) {
  if (!name || typeof name !== 'string') return '';
  return name.trim().toLowerCase().replace(/\s+/g, '-');
}

/** Когда таблица vendors пуста — резолвим вендора по slug из поля product.vendor */
export function resolveVendorBySlug(catalogProducts, slug) {
  if (!slug || typeof slug !== 'string') return null;
  const want = slug.trim().toLowerCase();
  if (!want) return null;

  const byName = new Map();
  for (const p of ensureArray(catalogProducts)) {
    const name = (p.vendor || '').trim();
    if (!name) continue;
    const key = name.toLowerCase();
    if (!byName.has(key)) byName.set(key, { name, products: [] });
    byName.get(key).products.push(p);
  }

  for (const { name, products } of byName.values()) {
    if (slugifyVendorName(name) === want) {
      return { name, slug: want, products };
    }
  }
  return null;
}

export function normalizeVendorApiProducts(products, vendorName) {
  if (!Array.isArray(products) || !vendorName) return [];
  return products.map((p) => ({
    ...p,
    vendor: vendorName,
    shortDescription: p.short_description ?? p.shortDescription ?? '',
  }));
}

/** Уровень правой панели каталога (навигация, не фильтры). */
export function getCatalogViewLevel({
  searchQuery,
  categoryFilter,
  vendorFilter,
  vendorSlugParam,
  sectionFilter,
  situationFilter = null,
}) {
  if (vendorSlugParam) return 'vendor';
  if (searchQuery?.trim()) return 'search';
  // Ситуация — отдельный режим: список всех подходящих решений
  if (situationFilter != null) return 'search';
  if (categoryFilter !== 'all' && sectionFilter !== 'all') return 'vendors';
  if (categoryFilter !== 'all') return 'sections';
  return 'root';
}

export function shouldShowSolutionsList({
  searchQuery,
  categoryFilter,
  situationFilter,
  vendorFilter,
  vendorSlugParam,
  sectionFilter,
}) {
  const level = getCatalogViewLevel({
    searchQuery,
    categoryFilter,
    vendorFilter,
    vendorSlugParam,
    sectionFilter: sectionFilter ?? 'all',
    situationFilter,
  });
  return level === 'vendor' || level === 'search';
}

/**
 * Категория/раздел и ситуация — взаимозаменяемые режимы фильтрации, не комбинируются.
 * Активная ситуация вытесняет taxonomy; активная категория/раздел вытесняет ситуацию.
 */
export function resolveExclusiveCatalogFilters({
  situationFilter = null,
  categoryFilter = 'all',
  sectionFilter = 'all',
}) {
  if (situationFilter != null && situationFilter !== '') {
    return {
      mode: 'situation',
      situationFilter,
      categoryFilter: 'all',
      sectionFilter: 'all',
    };
  }
  return {
    mode: 'taxonomy',
    situationFilter: null,
    categoryFilter: categoryFilter || 'all',
    sectionFilter: sectionFilter || 'all',
  };
}

export function filterCatalogItems({
  items,
  situationFilter,
  categoryFilter,
  searchQuery,
  targetVendor,
  skipVendorMatch = false,
}) {
  const q = (searchQuery || '').trim().toLowerCase();
  return items.filter((item) => {
    const matchesSituation =
      situationFilter === null ||
      (Array.isArray(item.situations) && item.situations.includes(situationFilter));
    const matchesCategory = categoryFilter === 'all' || item.category === categoryFilter;
    const matchesVendor =
      skipVendorMatch || targetVendor === null || item.vendor === targetVendor;
    const matchesSearch =
      !q ||
      item.name?.toLowerCase().includes(q) ||
      item.short_description?.toLowerCase().includes(q) ||
      item.shortDescription?.toLowerCase().includes(q) ||
      item.vendor?.toLowerCase().includes(q) ||
      item.category?.toLowerCase().includes(q) ||
      (Array.isArray(item.tags) && item.tags.some((tag) => String(tag).toLowerCase().includes(q)));
    return matchesSituation && matchesCategory && matchesVendor && matchesSearch;
  });
}

/** Единый empty-state для пустых категории / раздела / списка решений. */
export const CATALOG_EMPTY_MESSAGE = 'Ничего не найдено';

/**
 * Имена вендоров для сетки каталога.
 * При активном фильтре категории/раздела/ситуации не подмешиваем весь GET /vendors —
 * иначе пустой раздел выглядит заполненным чужими вендорами.
 */
export function resolveGridVendorNames({
  vendorList,
  vendorsFromApi,
  categoryFilter = 'all',
  sectionFilter = 'all',
  situationFilter = null,
}) {
  const fromProducts = Array.isArray(vendorList) ? vendorList.filter(Boolean) : [];
  if (fromProducts.length > 0) return fromProducts;

  const isDrillDown =
    situationFilter != null ||
    (categoryFilter && categoryFilter !== 'all') ||
    (sectionFilter && sectionFilter !== 'all');
  if (isDrillDown) return [];

  const fromApi = ensureArray(vendorsFromApi)
    .map((v) => (v.name || '').trim())
    .filter(Boolean);
  return [...new Set(fromApi)].sort((a, b) => a.localeCompare(b, 'ru'));
}
